import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import { FormEventHandler, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormItem } from '../../../common/hooks/use-form-item';
import { InputType } from '../../../common/hooks/use-form-item/types';
import { and } from '../../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../../common/hooks/use-form-item/validators/existential';
import { greaterOrEqualThan, maxLength } from '../../../common/hooks/use-form-item/validators/operational';
import { Tanium } from '../../../integrations/tanium/models/Tanium';
import { CheckTaniumIntegrationArgs, UpdateTaniumIntegrationArgs } from '../../../integrations/tanium/services/TaniumRepository';
import { DangerButton, PrimaryButton } from '../../../modal-flows/components/Buttons';
import { ACCORDION_STYLES } from '../../../common/components/IntegrationAccordion';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { url, urlWithoutTrailingSlash } from '../../../common/hooks/use-form-item/validators/type';

type Props = {
  tanium: Tanium | null;
  onTestClick: (args: CheckTaniumIntegrationArgs) => void;
  onConnectClick: (args: UpdateTaniumIntegrationArgs) => void;
  onUnlinkClick: () => void;
  canUpdate: boolean;
};

const DEFAULT_MAX_CONCURRENT_RETRIEVALS = 1;
const DEFAULT_REQUEST_RATE = 1;

export const TaniumAccordionForm = (props: Props) => {
  const { tanium, onConnectClick, onTestClick, canUpdate, onUnlinkClick } = props;
  const { t } = useTranslation();
  const isTaniumConfigured = Boolean(tanium);
  const [visible, setVisible] = useState<boolean>(false);

  const [instanceUrl, instanceUrlComponent, isInstanceUrlValid] = useFormItem({
    type: InputType.TYPE_TEXT,
    value: tanium?.getUrl() ?? '',
    inputOptions: {
      autoFocus: true,
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    validator: and(required(), url(), urlWithoutTrailingSlash(), maxLength(1000)),
    label: t('integrationList.tanium.instanceUrlLabel'),
  });
  const [accessToken, accessTokenComponent, isAccessTokenValid] = useFormItem({
    type: visible ? InputType.TYPE_TEXT : InputType.TYPE_PASSWORD,
    value: tanium?.getAccessToken() ?? '',
    inputOptions: {
      // Prevent browsers from trying to prefill the user's password in this field:
      // `one-time-code` seems to be the least unrelated value for a token, see
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
      autoComplete: 'one-time-code',
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    validator: and(required(), maxLength(1000)),
    label: t('integrationList.tanium.accessTokenLabel'),
  });
  const [certificate, certificateComponent] = useFormItem({
    type: InputType.TYPE_TEXTAREA,
    inputOptions: {
      multiline: true,
      disabled: !canUpdate,
      help: t('integrationList.optional'),
    },
    value: tanium?.getTrustedCaCertificate() ?? '',
    validator: maxLength(10_000),
    label: t('integrationList.tanium.certificateLabel'),
  });
  const [maxConcurrent, maxConcurrentComponent, isMaxConcurrentValid] = useFormItem({
    type: InputType.TYPE_NUMBER,
    value: tanium?.getMaxConcurrentRetrievals() ?? DEFAULT_MAX_CONCURRENT_RETRIEVALS,
    inputOptions: {
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    validator: and(required(), greaterOrEqualThan(DEFAULT_MAX_CONCURRENT_RETRIEVALS)),
    label: t('integrationList.tanium.maxConcurrentLabel'),
  });
  const [requestRate, requestRateComponent, isRequestRateValid] = useFormItem({
    type: InputType.TYPE_NUMBER,
    value: tanium?.getRequestRate() ?? DEFAULT_REQUEST_RATE,
    inputOptions: {
      disabled: !canUpdate,
      showRequiredIndicator: true,
    },
    validator: and(required(), greaterOrEqualThan(DEFAULT_REQUEST_RATE)),
    label: t('integrationList.tanium.requestRateLabel'),
  });

  const isFormValid = isAccessTokenValid && isInstanceUrlValid && isMaxConcurrentValid && isRequestRateValid;
  const fieldsModified =
    instanceUrl !== tanium?.getUrl() ||
    accessToken !== tanium?.getAccessToken() ||
    certificate !== tanium.getTrustedCaCertificate() ||
    maxConcurrent !== tanium?.getMaxConcurrentRetrievals() ||
    requestRate !== tanium.getRequestRate();
  const canSubmit = isFormValid && fieldsModified;

  const handleConnectButtonClick: FormEventHandler = (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    onConnectClick({
      accessToken,
      url: instanceUrl,
      trustedCaCertificate: certificate,
      requestRate,
      maxConcurrentRetrievals: maxConcurrent,
    });
  };

  const handleTestClick = () => {
    if (!isFormValid) return;

    onTestClick({
      accessToken,
      url: instanceUrl,
      trustedCaCertificate: certificate,
    });
  };

  const toggleVisibility = () => {
    setVisible((prevValue) => !prevValue);
  };

  return (
    <Box sx={{ p: 3, backgroundImage: ACCORDION_STYLES.backgroundImage }}>
      <Stack spacing={1.5} component="form" onSubmit={handleConnectButtonClick}>
        {instanceUrlComponent}
        <Stack direction="row" spacing={1} alignItems="end">
          {accessTokenComponent}
          <IconButton
            data-role="access-token-toggle"
            size="small"
            title={t(visible ? 'toggleVisibility.visible' : 'toggleVisibility.hidden', { field: t('integrationList.tanium.accessTokenLabel') })}
            disabled={!accessToken}
            onClick={toggleVisibility}
          >
            {visible ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </Stack>
        {certificateComponent}
        {maxConcurrentComponent}
        {requestRateComponent}
        <Divider />
        <Stack direction="row" gap={2}>
          {canUpdate ? (
            <>
              {isTaniumConfigured && <DangerButton onClick={onUnlinkClick}>{t('integrationList.tanium.unlinkButton')}</DangerButton>}
              <PrimaryButton disabled={!isFormValid} onClick={handleTestClick} sx={{ ml: 'auto' }}>
                {t('integrationList.tanium.testButton')}
              </PrimaryButton>
              <PrimaryButton disabled={!canSubmit} type="submit">
                {t('integrationList.tanium.connectButton')}
              </PrimaryButton>
            </>
          ) : (
            <Typography>{t('integrationList.noPermission')}</Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};
